/**
 * Phase 3G-B — React Error Boundary (boutique fallback).
 *
 * Wraps customer + admin route trees. When a child throws during render
 * (or in a lifecycle method), shows a calmly-styled boutique fallback
 * instead of the dreaded white screen. Logs the crash to
 * `/api/errors/log` so SRE can investigate (collection: `error_logs`).
 *
 * Boutique aesthetic — soft cream wash, hand-drawn rosegold thread &
 * needle SVG, serif headline, calm tone. The "technical details"
 * panel is tucked behind a subtle disclosure so customers never see
 * stack traces unless they ask for them. Admin scope keeps the same
 * vibe but uses copy that's appropriate for an internal user.
 *
 * Usage:
 *   <ErrorBoundary scope="customer"><Suspense>…</Suspense></ErrorBoundary>
 *   <ErrorBoundary scope="admin">…</ErrorBoundary>
 */
import React from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, copied: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        // Best-effort logging — never crash the boundary itself.
        try {
            const payload = {
                source: "frontend",
                scope: this.props.scope || "unknown",
                message: String(error?.message || error || "Unknown error").slice(0, 900),
                stack: String(error?.stack || "").slice(0, 8000),
                component_stack: String(info?.componentStack || "").slice(0, 4000),
                url: typeof window !== "undefined" ? window.location.href.slice(0, 500) : "",
                user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 300) : "",
            };
            // Fire-and-forget; ignore any failure (we're already in a crashed state).
            fetch(`${API}/errors/log`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify(payload),
                keepalive: true,
            }).catch(() => { });
            if (typeof console !== "undefined" && console.error) {
                console.error("[ErrorBoundary]", error, info);
            }
        } catch {
            /* swallow */
        }
    }

    handleReload = () => {
        if (typeof window !== "undefined") window.location.reload();
    };

    handleHome = () => {
        if (typeof window !== "undefined") {
            const isAdmin = this.props.scope === "admin";
            window.location.href = isAdmin ? "/config" : "/";
        }
    };

    handleCopy = () => {
        try {
            const txt = `Error: ${this.state.error?.message || this.state.error}\nURL: ${window.location.href}\nUA: ${navigator.userAgent}`;
            navigator.clipboard?.writeText(txt);
            this.setState({ copied: true });
            setTimeout(() => this.setState({ copied: false }), 2000);
        } catch { /* noop */ }
    };

    render() {
        if (!this.state.hasError) return this.props.children;
        const isAdmin = this.props.scope === "admin";
        const msg = String(this.state.error?.message || this.state.error || "Something went wrong");

        return (
            <div
                data-testid={`error-boundary-${this.props.scope || "root"}`}
                className="min-h-[80vh] flex items-center justify-center px-4 py-12 relative overflow-hidden"
                style={{
                    background:
                        "radial-gradient(ellipse at top, rgba(201,164,122,0.18) 0%, transparent 55%)," +
                        "linear-gradient(180deg, #FAF6F1 0%, #F7EEE3 100%)",
                }}
            >
                {/* Soft decorative thread that drifts across the band —
                    matches the boutique's "empty cart" illustration so the
                    whole site speaks the same visual language. */}
                <BoutiqueThread />

                <div className="relative max-w-lg w-full text-center">
                    {/* Hand-drawn needle + thread mark — the only graphic.
                        Built in SVG so it crisps perfectly on retina and
                        respects the rosegold palette. */}
                    <BoutiqueGlyph className="mx-auto mb-7" />

                    <div
                        className="text-[10px] uppercase tracking-[0.32em] text-rosegold font-bold mb-3"
                        data-testid="error-boundary-kicker"
                    >
                        {isAdmin ? "Admin · A loose thread" : "A small detour"}
                    </div>

                    <h2 className="font-display text-3xl sm:text-[40px] text-ink font-light leading-tight mb-3">
                        {isAdmin ? <>The atelier <em className="not-italic text-wine">paused</em>.</>
                                 : <>The atelier <em className="not-italic text-wine">paused</em>.</>}
                    </h2>

                    <p className="text-[13.5px] sm:text-sm text-ink-soft leading-relaxed max-w-md mx-auto mb-7 font-serif italic">
                        {isAdmin
                            ? '"A loose thread caught one of the looms. The crash has been logged for our atelier crew. A quick refresh usually weaves things back together."'
                            : '"Even our finest stitching catches a thread sometimes. Your cart, wishlist and orders are all safe — a gentle refresh is all this page needs."'}
                    </p>

                    <div className="flex flex-wrap items-center justify-center gap-2.5 mb-6">
                        <button
                            onClick={this.handleReload}
                            data-testid="error-boundary-reload"
                            className="h-11 px-6 rounded-full text-[11.5px] font-bold uppercase tracking-[0.22em] bg-ink text-ivory hover:bg-wine transition-colors shadow-[0_8px_24px_-12px_rgba(26,24,24,0.45)]"
                        >
                            Refresh the page
                        </button>
                        <button
                            onClick={this.handleHome}
                            data-testid="error-boundary-home"
                            className="h-11 px-6 rounded-full text-[11.5px] font-bold uppercase tracking-[0.22em] border border-rosegold/40 bg-white text-ink hover:bg-ivory-soft hover:border-rosegold transition-colors"
                        >
                            {isAdmin ? "Back to admin" : "Boutique home"}
                        </button>
                    </div>

                    {/* Reassurance row — three calm pills the customer
                        recognises from every other empty / error surface
                        on the site. Keeps the tone warm and grounded. */}
                    {!isAdmin && (
                        <ul
                            className="inline-flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[11px] text-ink-soft mb-6"
                            data-testid="error-boundary-reassurance"
                        >
                            <li className="inline-flex items-center gap-1.5">
                                <Dot /> Your cart is safe
                            </li>
                            <li className="inline-flex items-center gap-1.5">
                                <Dot /> Wishlist preserved
                            </li>
                            <li className="inline-flex items-center gap-1.5">
                                <Dot /> Orders unaffected
                            </li>
                        </ul>
                    )}

                    {/* Technical details — folded away by default so
                        customers never see a stack trace. Admins can
                        still expand it inline (same component, same
                        behaviour) when they need to copy a payload. */}
                    <details
                        className="text-left mt-2 max-w-md mx-auto"
                        data-testid="error-boundary-details"
                    >
                        <summary
                            className="text-[10.5px] uppercase tracking-[0.2em] text-rosegold-deep/70 cursor-pointer hover:text-rosegold-deep text-center font-semibold"
                            data-testid="error-boundary-details-toggle"
                        >
                            Show technical details
                        </summary>
                        <div className="mt-3 rounded-2xl border border-rosegold/15 bg-white/70 backdrop-blur-sm p-3">
                            <pre className="text-[10.5px] text-ink-soft whitespace-pre-wrap break-words font-mono max-h-40 overflow-auto leading-relaxed">
{msg}
                            </pre>
                            <button
                                onClick={this.handleCopy}
                                data-testid="error-boundary-copy"
                                className="mt-2 text-[10px] uppercase tracking-[0.18em] font-bold text-rosegold hover:text-wine"
                            >
                                {this.state.copied ? "Copied ✓" : "Copy details"}
                            </button>
                        </div>
                    </details>

                    {!isAdmin && (
                        <p
                            className="text-[11px] text-ink-soft/70 mt-7"
                            data-testid="error-boundary-help"
                        >
                            Still stuck? Reach our atelier on{" "}
                            <a
                                href="https://wa.me/919876543210"
                                className="text-rosegold-deep font-semibold hover:underline"
                            >
                                WhatsApp
                            </a>
                            {" "}— we usually reply within a few minutes.
                        </p>
                    )}
                </div>
            </div>
        );
    }
}

/** Hand-drawn needle + thread glyph — pure SVG, no external assets. */
function BoutiqueGlyph({ className = "" }) {
    return (
        <svg
            viewBox="0 0 140 140"
            className={className}
            width="116"
            height="116"
            aria-hidden="true"
        >
            <defs>
                <linearGradient id="thread-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#C9A47A" />
                    <stop offset="55%" stopColor="#8B2A4B" />
                    <stop offset="100%" stopColor="#5C1A33" />
                </linearGradient>
                <radialGradient id="halo" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="rgba(201,164,122,0.35)" />
                    <stop offset="100%" stopColor="rgba(201,164,122,0)" />
                </radialGradient>
            </defs>
            {/* Soft halo */}
            <circle cx="70" cy="70" r="62" fill="url(#halo)" />
            {/* Outer ring */}
            <circle
                cx="70"
                cy="70"
                r="54"
                fill="none"
                stroke="url(#thread-grad)"
                strokeWidth="0.8"
                opacity="0.45"
                strokeDasharray="2 4"
            />
            {/* Thread — a flowing curve that loops through the needle eye */}
            <path
                d="M 20 92 C 38 70, 56 102, 78 78 C 98 56, 80 36, 102 32"
                fill="none"
                stroke="url(#thread-grad)"
                strokeWidth="1.4"
                strokeLinecap="round"
            />
            {/* Needle body */}
            <line
                x1="98"
                y1="36"
                x2="118"
                y2="28"
                stroke="#1A1818"
                strokeWidth="1.6"
                strokeLinecap="round"
            />
            {/* Needle eye */}
            <ellipse
                cx="102.5"
                cy="34.5"
                rx="2"
                ry="3"
                fill="none"
                stroke="#1A1818"
                strokeWidth="0.9"
                transform="rotate(-22 102.5 34.5)"
            />
            {/* Needle tip (subtle taper) */}
            <line
                x1="118"
                y1="28"
                x2="121"
                y2="26.8"
                stroke="#1A1818"
                strokeWidth="0.7"
                strokeLinecap="round"
            />
            {/* Stitches along the thread */}
            {[28, 38, 50, 62, 72].map((x, i) => (
                <line
                    key={i}
                    x1={x}
                    y1={88 - i * 3}
                    x2={x + 4}
                    y2={86 - i * 3}
                    stroke="#8B2A4B"
                    strokeWidth="0.8"
                    strokeLinecap="round"
                    opacity={0.55 + i * 0.05}
                />
            ))}
            {/* Two tiny pearls / dots like in the empty-cart illustration */}
            <circle cx="60" cy="88" r="1.6" fill="#8B2A4B" opacity="0.6" />
            <circle cx="34" cy="82" r="1.2" fill="#C9A47A" opacity="0.8" />
        </svg>
    );
}

/** Decorative drifting thread used as a watermark behind the panel. */
function BoutiqueThread() {
    return (
        <svg
            viewBox="0 0 1200 600"
            className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.18]"
            aria-hidden="true"
            preserveAspectRatio="xMidYMid slice"
        >
            <defs>
                <linearGradient id="bg-thread" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(201,164,122,0)" />
                    <stop offset="50%" stopColor="rgba(201,164,122,0.55)" />
                    <stop offset="100%" stopColor="rgba(201,164,122,0)" />
                </linearGradient>
            </defs>
            <path
                d="M -50 320 C 200 240, 360 420, 600 320 S 1000 220, 1250 300"
                fill="none"
                stroke="url(#bg-thread)"
                strokeWidth="1"
                strokeDasharray="3 6"
            />
            <path
                d="M -50 360 C 220 280, 380 460, 620 360 S 1020 260, 1260 340"
                fill="none"
                stroke="url(#bg-thread)"
                strokeWidth="0.6"
                strokeDasharray="2 8"
            />
        </svg>
    );
}

/** Tiny rosegold dot used between reassurance pills. */
function Dot() {
    return (
        <span
            className="w-1 h-1 rounded-full bg-rosegold inline-block"
            aria-hidden="true"
        />
    );
}

export default ErrorBoundary;
